/**
 * Filter panel (PRD "Filter control", §5.2).
 *
 * Post type · content type · author (group views) · performance tier · length range · has-media,
 * plus a read-only echo of the Counted-post-types list — the one control that moves baselines,
 * edited from the Metrics header popover. Display filters never do (§5.2) — that distinction is
 * stated in the panel itself so it is never implicit.
 */

import {
  CONTENT_TYPES,
  LENGTH_UNITS,
  MAX_PERF_CONDITIONS,
  METRIC_KEYS,
  POST_TYPES,
  type ContentType,
  type LengthUnit,
  type MetricKey,
  type PerfConditionDTO,
  type PostType,
  type ViewFiltersState,
  type ViewMemberDTO,
} from '@lw/shared';
import { CheckboxList } from '../../components';
import { CONTENT_TYPE_LABEL, METRIC_LABEL, POST_TYPE_LABEL } from '../../lib/labels';

const PERF_PRESETS = [0.5, 2, 5, 10];

export type FilterPanelProps = {
  filters: ViewFiltersState;
  onChange: (next: ViewFiltersState) => void;
  isGroup: boolean;
  members: ViewMemberDTO[];
  /** Live counted list from settings — shown read-only; edited in the Metrics header popover. */
  countedPostTypes: PostType[];
};

export function FilterPanel({
  filters,
  onChange,
  isGroup,
  members,
  countedPostTypes,
}: FilterPanelProps) {
  const patch = (next: Partial<ViewFiltersState>) => onChange({ ...filters, ...next });

  /**
   * The panel keeps its single-condition control: it edits the **first multiplier** condition and
   * leaves everything else in `perf` alone, so a richer selection made in the feed sidebar survives
   * a visit to this popover instead of being silently truncated to one row.
   */
  const perfCondition = filters.perf?.find((condition) => condition.kind === 'multiplier');

  const setPerf = (metric: MetricKey | '', min: number | null) => {
    const current = filters.perf ?? [];
    const replacement: PerfConditionDTO | null =
      metric === '' || min === null || !Number.isFinite(min)
        ? null
        : { metric, kind: 'multiplier', min };
    // Everything the control does not own stays, minus any row the replacement would duplicate.
    const others = current.filter(
      (condition) =>
        condition !== perfCondition &&
        !(
          replacement !== null &&
          condition.metric === replacement.metric &&
          condition.kind === 'multiplier'
        ),
    );
    const kept = replacement === null ? others : [replacement, ...others];

    const next: ViewFiltersState = { ...filters };
    if (kept.length === 0) delete next.perf;
    else next.perf = kept.slice(0, MAX_PERF_CONDITIONS);
    onChange(next);
  };

  const setLength = (unit: LengthUnit | '', min: string, max: string) => {
    const next: ViewFiltersState = { ...filters };
    const parsedMin = min.trim() === '' ? undefined : Math.max(0, Math.trunc(Number(min)));
    const parsedMax = max.trim() === '' ? undefined : Math.max(0, Math.trunc(Number(max)));
    if (unit === '' || (parsedMin === undefined && parsedMax === undefined)) {
      delete next.lenUnit;
      delete next.lenMin;
      delete next.lenMax;
    } else {
      next.lenUnit = unit;
      if (parsedMin === undefined || Number.isNaN(parsedMin)) delete next.lenMin;
      else next.lenMin = parsedMin;
      if (parsedMax === undefined || Number.isNaN(parsedMax)) delete next.lenMax;
      else next.lenMax = parsedMax;
    }
    onChange(next);
  };

  const setHasMedia = (value: string) => {
    const next: ViewFiltersState = { ...filters };
    if (value === 'any') delete next.hasMedia;
    else next.hasMedia = value === 'yes';
    onChange(next);
  };

  /**
   * `edited` / `hasLink` / `saved` are plain tri-state selects — "any" removes the param entirely
   * (§5.2). `saved` is a display filter like the other two: it narrows the list, never a baseline.
   */
  const setTriState = (key: 'edited' | 'hasLink' | 'saved', value: string) => {
    const next: ViewFiltersState = { ...filters };
    if (value === 'any') delete next[key];
    else next[key] = value === 'yes';
    onChange(next);
  };

  const triValue = (value: boolean | undefined): string =>
    value === undefined ? 'any' : value ? 'yes' : 'no';

  const clearAll = () => {
    const q = filters.q;
    onChange(q !== undefined && q.length > 0 ? { q } : {});
  };

  const lenUnit = filters.lenUnit ?? '';

  return (
    <div className="filter-panel">
      <div className="filter-panel__cols">
        <section className="filter-panel__block">
          <h4 className="filter-panel__title">Post type</h4>
          <CheckboxList
            options={POST_TYPES.map((type) => ({
              value: type,
              label: POST_TYPE_LABEL[type],
              hint: countedPostTypes.includes(type) ? 'counted' : undefined,
            }))}
            selected={filters.postTypes ?? []}
            onChange={(next: PostType[]) =>
              patch({ postTypes: next.length > 0 ? next : undefined })
            }
          />
        </section>

        <section className="filter-panel__block">
          <h4 className="filter-panel__title">Content type</h4>
          <CheckboxList
            options={CONTENT_TYPES.map((type) => ({
              value: type,
              label: CONTENT_TYPE_LABEL[type],
            }))}
            selected={filters.contentTypes ?? []}
            onChange={(next: ContentType[]) =>
              patch({ contentTypes: next.length > 0 ? next : undefined })
            }
          />
        </section>

        {isGroup ? (
          <section className="filter-panel__block">
            <h4 className="filter-panel__title">Author</h4>
            <CheckboxList
              options={members.map((member) => ({
                value: member.id,
                label: member.name ?? `Profile #${member.id}`,
              }))}
              selected={filters.authorIds ?? []}
              onChange={(next: number[]) =>
                patch({ authorIds: next.length > 0 ? next : undefined })
              }
              emptyLabel="This group has no active members."
            />
          </section>
        ) : null}

        <section className="filter-panel__block">
          <h4 className="filter-panel__title">Performance</h4>
          <div className="filter-panel__row">
            <select
              className="select"
              aria-label="Performance metric"
              value={perfCondition?.metric ?? ''}
              onChange={(event) =>
                setPerf(
                  event.target.value as MetricKey | '',
                  event.target.value === '' ? null : (perfCondition?.min ?? 2),
                )
              }
            >
              <option value="">Any</option>
              {METRIC_KEYS.map((metric) => (
                <option key={metric} value={metric}>
                  {METRIC_LABEL[metric]}
                </option>
              ))}
            </select>
            <span className="muted">≥</span>
            <input
              className="input input--narrow"
              type="number"
              step="0.5"
              min="0"
              aria-label="Minimum multiplier"
              disabled={perfCondition === undefined}
              value={perfCondition?.min ?? ''}
              onChange={(event) =>
                setPerf(
                  perfCondition?.metric ?? '',
                  event.target.value === '' ? null : Number(event.target.value),
                )
              }
            />
            <span className="muted">×</span>
          </div>
          <div className="filter-panel__row">
            {PERF_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className="table-action"
                onClick={() => setPerf(perfCondition?.metric ?? 'reactions', preset)}
              >
                <span className="table-action__label">≥{preset}×</span>
              </button>
            ))}
          </div>
          <p className="field__hint">
            Multipliers compare a post to its own author's baseline; filtering by them never changes
            that baseline.
          </p>
        </section>

        <section className="filter-panel__block">
          <h4 className="filter-panel__title">Length</h4>
          <div className="filter-panel__row">
            <select
              className="select"
              aria-label="Length unit"
              value={lenUnit}
              onChange={(event) =>
                setLength(
                  event.target.value as LengthUnit | '',
                  String(filters.lenMin ?? ''),
                  String(filters.lenMax ?? ''),
                )
              }
            >
              <option value="">Any</option>
              {LENGTH_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit === 'words' ? 'Words' : 'Characters'}
                </option>
              ))}
            </select>
            <input
              className="input input--narrow"
              type="number"
              min="0"
              placeholder="min"
              aria-label="Minimum length"
              disabled={lenUnit === ''}
              value={filters.lenMin ?? ''}
              onChange={(event) =>
                setLength(lenUnit, event.target.value, String(filters.lenMax ?? ''))
              }
            />
            <input
              className="input input--narrow"
              type="number"
              min="0"
              placeholder="max"
              aria-label="Maximum length"
              disabled={lenUnit === ''}
              value={filters.lenMax ?? ''}
              onChange={(event) =>
                setLength(lenUnit, String(filters.lenMin ?? ''), event.target.value)
              }
            />
          </div>

          <h4 className="filter-panel__title">Media</h4>
          <select
            className="select"
            aria-label="Has media"
            value={triValue(filters.hasMedia)}
            onChange={(event) => setHasMedia(event.target.value)}
          >
            <option value="any">Any</option>
            <option value="yes">With media</option>
            <option value="no">Without media</option>
          </select>

          <h4 className="filter-panel__title">Post flags</h4>
          <div className="filter-panel__row">
            <select
              className="select"
              aria-label="Edited"
              value={triValue(filters.edited)}
              onChange={(event) => setTriState('edited', event.target.value)}
            >
              <option value="any">Any edit state</option>
              <option value="yes">Edited</option>
              <option value="no">Never edited</option>
            </select>
            <select
              className="select"
              aria-label="Has external link"
              value={triValue(filters.hasLink)}
              onChange={(event) => setTriState('hasLink', event.target.value)}
            >
              <option value="any">Any link state</option>
              <option value="yes">With external link</option>
              <option value="no">Without external link</option>
            </select>
            <select
              className="select"
              aria-label="Saved"
              value={triValue(filters.saved)}
              onChange={(event) => setTriState('saved', event.target.value)}
            >
              <option value="any">Saved or not</option>
              <option value="yes">Saved only</option>
              <option value="no">Not saved</option>
            </select>
          </div>
        </section>
      </div>

      <footer className="filter-panel__footer">
        <div className="filter-panel__counted">
          <span className="muted">Counted for baselines &amp; aggregates:</span>{' '}
          {countedPostTypes.length === 0
            ? '—'
            : countedPostTypes.map((type) => POST_TYPE_LABEL[type]).join(', ')}
        </div>
        <button type="button" className="table-action" onClick={clearAll}>
          <span className="table-action__label">Clear all</span>
        </button>
      </footer>
    </div>
  );
}

export default FilterPanel;
